import React, { useEffect, useRef, useState } from 'react'
import { AuthProvider, useAuth } from './hooks/useAuth'
// eslint-disable-next-line no-unused-vars
import { LoginScreen, InviteAcceptScreen, SuperAdminSetup } from './components/Auth'
import OnboardingFlow from './components/Onboarding'
import LandingPage from './components/LandingPage'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import DeleteAccountPage from './components/DeleteAccountPage'
import SupportPage from './components/SupportPage'
import BibleApp from './BibleApp'
import AdminPanel from './components/AdminPanel'
import LaunchScreen from './components/LaunchScreen'
import OnbInviteWelcome from './components/OnbInviteWelcome'
import JoinChurchPage from './components/JoinChurchPage'
import { captureDeepLink } from './deepLink'
import { supabase } from './supabase'
import Median from 'median-js-bridge'

// Capture any push-notification deep link (?screen=...) at cold start, before
// routing can redirect a logged-out user to /login and drop the query string.
// BibleApp reads it back once auth + church data have loaded.
captureDeepLink()

// Secret setup key - change this to something only you know
// Access via: https://yourapp.com/?setup=nlc-admin-setup-2024
const SETUP_KEY = 'nlc-admin-setup-2024'

// Launch screen animation (redesign Phase 8a) — once per real cold start,
// not per in-app SPA navigation. A module-level flag (not state) survives
// across AppInner re-renders but resets on a genuine page reload.
let hasShownLaunchThisLoad = false
const LAUNCH_SKIP_PATHS = new Set(['/privacy', '/terms', '/delete-account', '/support', '/admin'])

// ── Path helpers ────────────────────────────────────────────────────────
// Normalise pathname (strip trailing slashes, default to '/') so route
// comparisons elsewhere don't have to repeat the dance.
function getNormalisedPath() {
  if (typeof window === 'undefined') return '/'
  const p = window.location.pathname.replace(/\/+$/, '')
  return p === '' ? '/' : p
}

// SPA-style navigation: push the new URL and let AppInner re-render
// against the popstate event we dispatch manually.
//
// `replace:true` swaps the current history entry instead of pushing a new one.
// Use it for *automatic* redirects (a logged-in user bounced off '/' or
// '/login' to '/app', an OAuth callback URL, an admin gate kicking a
// logged-out visitor to '/login'). Those target screens render `null` and
// immediately redirect onward, so leaving them in history means the Android
// hardware back button lands on a dead, self-redirecting entry — which is how
// back-navigation could unwind into a blank/looping state and crash the app.
// Replacing keeps the history stack made only of real, renderable screens.
function navigateTo(path, { replace = false } = {}) {
  if (typeof window === 'undefined') return
  if (window.location.pathname === path) return
  if (replace) window.history.replaceState({}, '', path)
  else window.history.pushState({}, '', path)
  dispatchProgrammaticPopstate()
}

// Drive the pathname router via a synthetic popstate, flagged so BibleApp's
// in-app back-stack listener ignores it (it's a programmatic route change, not
// a hardware/browser Back press). Without the flag, the back-stack would run an
// undo (a setState) during this dispatch — sometimes mid-render — and blank the
// screen (notably on sign-out → /login).
function dispatchProgrammaticPopstate() {
  if (typeof window === 'undefined') return
  window.__nlcProgrammaticNav = true
  try { window.dispatchEvent(new PopStateEvent('popstate')) }
  finally { window.__nlcProgrammaticNav = false }
}

// Median (mobile webview wrapper) — when present we skip the marketing landing
// page entirely. The UA token and window.webkit.messageHandlers are both
// available on first paint; window.median is injected a few seconds later
// (per Median's own bootstrap timing), so don't rely on it alone or the
// launch screen / landing-page gating can flicker false on a cold start.
function isInsideMedian() {
  if (typeof window === 'undefined') return false
  if (/median|gonative/i.test(navigator.userAgent || '')) return true
  if (window.webkit && window.webkit.messageHandlers) return true
  return typeof window.median !== 'undefined'
}

// Apply a URL handed to us by Median's jsNavigation listener (a deep link or a
// push-notification tap while the app is backgrounded) as an in-app SPA
// navigation — no full page reload. We push the path+query into history and
// fire the same events the app already listens for: popstate (so AppInner's
// route state + the deep-link reader update) and nlc:deeplink (BibleApp's
// foreground deep-link re-read hook). The deep-link params in the query string
// are then picked up by parseDeepLinkFromSearch exactly as on cold start.
// True when this page is the Google OAuth return landing IN A BROWSER (not the
// app): the inline script in index.html stashed the token payload here.
function isBrowserOAuthReturn() {
  if (typeof window === 'undefined') return false
  try {
    if (/median|gonative/i.test(navigator.userAgent || '')) return false
    if (typeof window.median !== 'undefined') return false
    if (window.webkit && window.webkit.messageHandlers) return false
    return !!sessionStorage.getItem('nlc.oauthRet')
  } catch (_e) { return false }
}

// Shown in the system browser after Google sign-in. iOS blocks an *automatic*
// jump to a custom scheme, so the user taps this link (a gesture) to reopen the
// app. The token payload rides along as a query param (oauth_ret) so it survives
// the scheme hop; the app then establishes the session from it.
function ReturnToApp() {
  let payload = ''
  try { payload = sessionStorage.getItem('nlc.oauthRet') || '' } catch (_e) { /* ignore */ }
  // Short scheme URL (host-qualified to match the format push uses). The payload
  // is just the PKCE code now, so this stays well under any URL-length limit.
  const target = 'thebiblecommunity://thebiblecommunity.co.uk/login?app_oauth=1&oauth_ret='
    + encodeURIComponent(payload)
  // Bare-scheme probes. The website is thebiblecommunity.co.uk (with an "e") but
  // the iOS bundle id is uk.co.thbiblecommunity.app (NO "e"). The registered
  // custom scheme likely matches the bundle spelling, so test BOTH: whichever
  // one launches the app is the scheme we must use everywhere. (Push working
  // proves nothing here — push URLs are parsed in JS, never resolved by iOS.)
  const probe   = 'thebiblecommunity://login'   // with "e" (current code/push)
  const probeNo = 'thbiblecommunity://login'    // no "e" (matches bundle id)
  return (
    <div style={{minHeight:'100vh',background:'var(--cream)',color:'var(--ink)',display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',gap:16,fontFamily:"'Plus Jakarta Sans',sans-serif",padding:24,textAlign:'center'}}>
      <div style={{fontSize:19,fontWeight:800}}>Almost there</div>
      <div style={{fontSize:14,color:'var(--stone)',maxWidth:300,lineHeight:1.5}}>
        Tap below to finish signing in and return to The Bible Community.
      </div>
      <a href={target}
        style={{background:'var(--gold)',color:'var(--ink)',padding:'14px 28px',borderRadius:12,
          fontSize:15,fontWeight:800,textDecoration:'none'}}>
        Return to the app
      </a>
      {/* Diagnostic aids (safe to remove once sign-in works). */}
      <div style={{display:'flex',gap:18}}>
        <a href={probe}
          style={{fontSize:12,color:'var(--stone)',textDecoration:'underline'}}>
          Test link (with “e”)
        </a>
        <a href={probeNo}
          style={{fontSize:12,color:'var(--stone)',textDecoration:'underline'}}>
          Test link (no “e”)
        </a>
      </div>
      <div style={{fontSize:10,color:'var(--stone-dim)',maxWidth:320,wordBreak:'break-all',lineHeight:1.5,userSelect:'all'}}>
        {target.length} chars · {target.slice(0, 90)}{target.length > 90 ? '…' : ''}
      </div>
    </div>
  )
}

function applyMedianNavUrl(rawUrl) {
  if (typeof window === 'undefined' || !rawUrl) return
  let target
  try {
    // Handles both https URLs and custom-scheme deep links of the form
    // thebiblecommunity://thebiblecommunity.co.uk/login?code=… (the URL parser
    // extracts pathname/search/hash for schemes with a //host part).
    const u = new URL(rawUrl, window.location.origin)
    target = (u.pathname || '/') + u.search + u.hash
  } catch (_e) {
    target = String(rawUrl)   // not a full URL — best-effort use as-is
  }
  try {
    window.history.pushState({}, '', target)
    dispatchProgrammaticPopstate()
    window.dispatchEvent(new Event('nlc:deeplink'))
  } catch (_e) { /* ignore */ }
}

function AdminGate() {
  const { user, profile, loading } = useAuth()
  if (loading) return (
    <div style={{minHeight:'100vh',background:'var(--cream)',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'var(--stone)',fontSize:14,fontFamily:'Plus Jakarta Sans,sans-serif'}}>Loading…</div>
    </div>
  )
  if (!user) {
    if (typeof window !== 'undefined') {
      try { sessionStorage.setItem('nlc.postLoginRedirect', '/admin') } catch {}
      navigateTo('/login', { replace: true })
    }
    return null
  }
  if (profile && !['admin', 'super_admin'].includes(profile.role)) {
    return (
      <div style={{minHeight:'100vh',background:'#0a0a0f',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
        <div style={{textAlign:'center',fontFamily:'Inter,sans-serif',color:'#fff',maxWidth:420}}>
          <div style={{fontSize:42,marginBottom:14}}>🛑</div>
          <div style={{fontSize:22,fontWeight:800,marginBottom:8}}>Access denied</div>
          <div style={{fontSize:14,color:'rgba(255,255,255,0.55)',lineHeight:1.5,marginBottom:22}}>
            This area is restricted to church admins.
          </div>
          <a href="/app" onClick={e => { e.preventDefault(); navigateTo('/app') }}
            style={{
              display:'inline-block',background:'#E8C87A',color:'#1B2A4A',
              padding:'11px 22px',borderRadius:10,fontSize:13,fontWeight:800,
              textDecoration:'none',
            }}>Back to app</a>
        </div>
      </div>
    )
  }
  return <AdminPanel />
}

function AppInner() {
  const { user, profile, loading, refreshProfile, applyInviteToExistingUser } = useAuth()
  const [inviteToken, setInviteToken] = useState(null)
  const [inviteWelcomeDone, setInviteWelcomeDone] = useState(false)
  const [showSetup,   setShowSetup]   = useState(false)
  const [path,        setPath]        = useState(getNormalisedPath())
  const [showLaunch,  setShowLaunch]  = useState(() => {
    if (!isInsideMedian()) return false
    if (hasShownLaunchThisLoad) return false
    if (typeof window === 'undefined') return false
    if (LAUNCH_SKIP_PATHS.has(getNormalisedPath())) return false
    if (new URLSearchParams(window.location.search).get('setup') === SETUP_KEY) return false
    return true
  })

  // Whether a signed-in user still needs the onboarding flow. Covers brand-new
  // OAuth (Apple/Google) users whose onboarding_complete is null and who have
  // no church yet — without this they fell into a gap (not === false, not
  // === true) and got a blank page. A null onboarding_complete with a church
  // is a legacy "already set up" user, so it stays out of onboarding.
  const inviteComplete = profile && profile.full_name && profile.church_id
  const needsOnboarding = !!user && !inviteComplete && (
    !profile ||
    profile.onboarding_complete === false ||
    (profile.onboarding_complete !== true && !profile.church_id)
  )

  // Median SPA navigation: when the native wrapper hands us a URL (a deep link
  // or a push-notification tap while backgrounded), route it in-app via React
  // state instead of letting the WebView do a full page reload. No-ops outside
  // the Median app (the listener simply never fires on the web).
  useEffect(() => {
    let listenerId = null
    try {
      if (Median.jsNavigation && Median.jsNavigation.url) {
        listenerId = Median.jsNavigation.url.addListener((data) => {
          // The bridge passes { url }; tolerate a bare string too.
          const url = data && typeof data === 'object' ? data.url : data
          applyMedianNavUrl(url)
        })
      }
    } catch (_e) { /* bridge not present (web) */ }
    return () => {
      try {
        if (listenerId && Median.jsNavigation && Median.jsNavigation.url) {
          Median.jsNavigation.url.removeListener(listenerId)
        }
      } catch (_e) { /* ignore */ }
    }
  }, [])

  // Push-notification taps: route the deep link IN-APP via Median's pushOpened
  // listener instead of letting OneSignal open it in the system browser.
  useEffect(() => {
    let id = null
    try {
      if (Median.onesignal && Median.onesignal.pushOpened) {
        id = Median.onesignal.pushOpened.addListener((data) => {
          try {
            console.log('pushOpened: ' + JSON.stringify(data || {}).slice(0, 220))
            const d = data || {}
            const ad = d.additionalData || d.additional_data || (d.notification && d.notification.additionalData) || {}
            const dl = ad.deeplink || ad.url || d.deeplink || d.url || d.launchURL || (d.notification && (d.notification.launchURL || d.notification.url))
            if (dl) applyMedianNavUrl(dl)
          } catch (e) { console.log('pushOpened err ' + (e && e.message)) }
        })
      }
    } catch (_e) { /* bridge not present */ }
    return () => {
      try { if (id && Median.onesignal && Median.onesignal.pushOpened) Median.onesignal.pushOpened.removeListener(id) } catch (_e) { /* ignore */ }
    }
  }, [])

  // Finish a Google OAuth exchange that bounced back into the app via the custom
  // scheme. A full navigation to /login?code=… is auto-handled by Supabase's
  // detectSessionInUrl; this covers the SPA/pushState delivery path and no-ops
  // once a session already exists (so it never double-spends the one-time code).
  // TEMP: surface the persistent nav log (written by index.html on every load)
  // so we can see what URL the post-OAuth blank page actually loaded.
  useEffect(() => {
    try {
      const nl = JSON.parse(localStorage.getItem('nlc.navlog') || '[]')
      console.log('NAVLOG (' + nl.length + '):')
      nl.forEach((h, i) => console.log('  ' + i + ': ' + h))
    } catch (_e) { /* ignore */ }
  }, [])

  useEffect(() => {
    const complete = async () => {
      try {
        // Never re-process once a session exists — protects the one-time code.
        const { data: sess } = await supabase.auth.getSession()
        if (sess?.session) return

        // (A) Direct custom-scheme return. Google OAuth now redirects to
        // thebiblecommunity://login, which reopens the app and is delivered as a
        // pushState via Median's jsNavigation → applyMedianNavUrl. PKCE returns
        // ?code=… (the webview that started the flow holds the verifier, so the
        // exchange works here); the implicit flow returns #access_token=… &
        // refresh_token=…. detectSessionInUrl can't see a pushState delivery, so
        // we finish it manually. Web is unaffected — there detectSessionInUrl
        // already ran on the full-page redirect.
        if (isInsideMedian()) {
          const hash = window.location.hash || ''
          const hp = new URLSearchParams(hash[0] === '#' ? hash.slice(1) : hash)
          const hAt = hp.get('access_token'), hRt = hp.get('refresh_token')
          if (hAt && hRt) {
            const { error } = await supabase.auth.setSession({ access_token: hAt, refresh_token: hRt })
            console.log('OAuth-return(hash): setSession ' + (error ? ('FAILED ' + error.message) : 'ok'))
            if (!error) window.history.replaceState({}, '', window.location.pathname + window.location.search)
            return
          }
          const directCode = new URLSearchParams(window.location.search).get('code')
          if (directCode) {
            const { error } = await supabase.auth.exchangeCodeForSession(directCode)
            console.log('OAuth-return(code): exchange ' + (error ? ('FAILED ' + error.message) : 'ok'))
            if (!error) {
              const u = new URL(window.location.href)
              u.searchParams.delete('code'); u.searchParams.delete('state')
              window.history.replaceState({}, '', u.pathname + u.search + u.hash)
            }
            return
          }
        }

        // (B) Legacy https-bounce path — token payload carried back as oauth_ret.
        const ret = new URLSearchParams(window.location.search).get('oauth_ret')
        if (!ret) return
        const p = new URLSearchParams(decodeURIComponent(ret))
        const at = p.get('access_token'), rt = p.get('refresh_token'), code = p.get('code')
        if (at && rt) {
          const { error } = await supabase.auth.setSession({ access_token: at, refresh_token: rt })
          console.log('OAuth-return: setSession ' + (error ? ('FAILED ' + error.message) : 'ok'))
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          console.log('OAuth-return: exchange ' + (error ? ('FAILED ' + error.message) : 'ok'))
        }
      } catch (e) { console.log('OAuth-return: error ' + (e?.message || e)) }
    }
    complete()
    window.addEventListener('nlc:deeplink', complete)
    return () => window.removeEventListener('nlc:deeplink', complete)
  }, [])

  // URL param parsing + popstate listener so back/forward + programmatic
  // navigateTo() both flow through the same `path` state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const invite = params.get('invite')
    const setup  = params.get('setup')
    if (invite) setInviteToken(invite)
    if (setup === SETUP_KEY) setShowSetup(true)
    const onPop = () => setPath(getNormalisedPath())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Apple/Google sign-in on the invite-accept screen (redesign Phase 8b) —
  // the token was stashed in sessionStorage before the OAuth redirect (see
  // InviteAcceptScreen in src/components/Auth.jsx) since OAuth doesn't carry
  // custom state through the round-trip the way the email+OTP path does via
  // signUpFromInvite's metadata. Apply it as soon as a session exists,
  // regardless of which of the OAuth-return paths above produced it —
  // needsOnboarding depends on profile.church_id, so this must run and
  // refresh the profile before the onboarding-routing effect below reads it.
  useEffect(() => {
    if (!user) return
    let token = null
    try { token = sessionStorage.getItem('nlc.inviteToken') } catch {}
    if (!token) return
    try { sessionStorage.removeItem('nlc.inviteToken') } catch {}
    ;(async () => {
      const { error } = await applyInviteToExistingUser(token)
      if (error) console.warn('applyInviteToExistingUser failed:', error.message || error)
      await refreshProfile()
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // After login, if the user originally hit /admin restore that location.
  // Otherwise route them to /app — but only once onboarding is finished,
  // so we don't unmount the OnboardingFlow mid name/photo/church flow.
  useEffect(() => {
    if (!user) return
    if (needsOnboarding) return
    try {
      const redir = sessionStorage.getItem('nlc.postLoginRedirect')
      if (redir === '/admin') {
        sessionStorage.removeItem('nlc.postLoginRedirect')
        navigateTo('/admin')
        return
      }
    } catch {}

    // Church-wide join link (redesign Phase 8b) — applied once, after login,
    // for someone who tapped /join/{slug} while logged out and just finished
    // signing up. Only applies if they don't already have a home church (an
    // existing invite/onboarding flow may have already set one).
    try {
      const joinSlug = sessionStorage.getItem('nlc.joinChurchSlug')
      if (joinSlug && !profile?.church_id) {
        sessionStorage.removeItem('nlc.joinChurchSlug')
        ;(async () => {
          const { data: church } = await supabase.from('churches')
            .select('id').eq('slug', joinSlug).maybeSingle()
          if (!church) return
          // join_church() decides open-vs-invite-only server-side (see
          // supabase-schema-security-church-id.sql) — no client-trusted
          // invite_only check needed any more.
          const { data: result } = await supabase.rpc('join_church', { p_church_id: church.id })
          if (result === 'joined') {
            await supabase.from('user_church_follows')
              .upsert({ user_id: user.id, church_id: church.id }, { onConflict: 'user_id,church_id' })
            await refreshProfile()
          }
        })()
      }
    } catch {}

    if (path === '/login' || path === '/') {
      navigateTo('/app', { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile])

  // Detect sign-out: after signing in via an invite link, the URL still
  // carries `?invite=TOKEN`. Without this, signing out would drop them
  // straight back onto InviteAcceptScreen because the gate below sees the
  // stale token + !user and re-renders the invite flow. Track whether we've
  // ever been authed this session so we only fire after a real sign-out
  // (not on initial mount when both `!user` and an invite token are valid).
  const wasUserRef = useRef(false)
  useEffect(() => {
    if (user) { wasUserRef.current = true; return }
    if (!wasUserRef.current) return
    wasUserRef.current = false
    setInviteToken(null)
    setInviteWelcomeDone(false)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      if (url.searchParams.has('invite')) {
        url.searchParams.delete('invite')
        window.history.replaceState({}, '', url.pathname + url.search + url.hash)
      }
    }
    navigateTo('/login')
  }, [user])

  // Logged-out user sitting on an authed path (e.g. just signed out of /app)
  // → send them to /login. Done in an EFFECT (post-commit) rather than during
  // render: navigating during render via the synthetic popstate is fragile
  // under React StrictMode's dev double-render and left a black screen on
  // sign-out locally. Public routes are left alone.
  useEffect(() => {
    if (loading || user) return
    const PUBLIC = new Set(['/', '/login', '/privacy', '/terms', '/delete-account', '/support'])
    if (!PUBLIC.has(path) && !inviteToken) navigateTo('/login', { replace: true })
  }, [loading, user, path, inviteToken])

  // Google OAuth landed back in the system browser — show the "return to app"
  // tap target (before any other routing/loading).
  if (isBrowserOAuthReturn()) return <ReturnToApp />

  // Launch screen animation — once per cold start. Returning signed-in users
  // (session restored) get only the brief logo moment; a fresh logged-out
  // launch gets the full verse sequence. Defaults to 'full' while auth is
  // still resolving so there's never a blank frame before we know which.
  if (showLaunch) {
    const variant = (!loading && user) ? 'brief' : 'full'
    return <LaunchScreen variant={variant} onDone={() => { hasShownLaunchThisLoad = true; setShowLaunch(false) }} />
  }

  if (loading) return (
    <div style={{minHeight:'100vh',background:'var(--cream)',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'var(--stone)',fontSize:14,fontFamily:'Plus Jakarta Sans,sans-serif'}}>Loading…</div>
    </div>
  )

  // ── /privacy route — public legal page, available to anyone whether
  // logged in or not. Handled before the invite/setup/auth gates so a
  // logged-in user clicking the in-app footer link goes straight here.
  if (path === '/privacy') return <PrivacyPolicy onNavigate={navigateTo}/>

  // ── /terms route — public legal page, same gating as /privacy.
  if (path === '/terms') return <TermsOfService onNavigate={navigateTo}/>

  // ── /delete-account route — public account-deletion page required by
  // Google Play / App Store. Same gating as /privacy: reachable signed in
  // or out so unauth visitors can read what deletion does, and signed-in
  // visitors can delete in place via the existing deleteAccount() flow.
  if (path === '/delete-account') return <DeleteAccountPage onNavigate={navigateTo}/>

  // ── /support route — public support page (contact + privacy link).
  if (path === '/support') return <SupportPage onNavigate={navigateTo}/>

  // ── /join/{slug} route — church-wide join link (redesign Phase 8b).
  // Public, reachable logged in or out; JoinChurchPage handles both.
  {
    const joinMatch = path.match(/^\/join\/([a-zA-Z0-9-]+)$/)
    if (joinMatch) return <div className="nlc-frame"><JoinChurchPage slug={joinMatch[1]} onNavigate={navigateTo}/></div>
  }

  // ── /admin route — super-admin only. Handled before any other gate so the
  // login redirect is preserved and members can't slip into it.
  if (path === '/admin') return <AdminGate />

  // Super admin first-time setup - only if correct secret key in URL
  if (showSetup && !user) return <div className="nlc-frame"><SuperAdminSetup /></div>

  // Someone arrived via an invite link — accept the invite regardless of
  // which path they landed on. Invite links are emailed pointing at root
  // so this also covers the legacy "https://.../?invite=TOKEN" shape.
  // Shows the "Welcome to [church]'s community" screen first (redesign
  // Phase 8a); tapping Join advances to the existing name/email/OTP flow.
  if (inviteToken && !user) {
    if (!inviteWelcomeDone) {
      return <div className="nlc-frame"><OnbInviteWelcome
        token={inviteToken}
        onJoin={() => setInviteWelcomeDone(true)}
        onNotYourChurch={() => {
          setInviteToken(null)
          setInviteWelcomeDone(false)
          const url = new URL(window.location.href)
          url.searchParams.delete('invite')
          window.history.replaceState({}, '', url.pathname + url.search + url.hash)
          navigateTo('/login')
        }}
      /></div>
    }
    return <div className="nlc-frame"><InviteAcceptScreen token={inviteToken} /></div>
  }

  // Self-service church creation has been removed — churches are added by
  // request only (the onboarding "Find your church" step has a "Request your
  // church" form). There is no ChurchOnboarding gate any more.

  // ── / (root) ──────────────────────────────────────────────────────
  // Public marketing landing page for logged-out web visitors. Median
  // users skip it entirely; logged-in users get bounced into /app.
  if (path === '/') {
    if (user) { navigateTo('/app', { replace: true }); return null }
    if (isInsideMedian()) { navigateTo('/login', { replace: true }); return null }
    return <LandingPage onNavigate={navigateTo}/>
  }

  // ── /login ────────────────────────────────────────────────────────
  // The existing welcome → email → OTP → name/photo/church flow lives
  // here. Keep OnboardingFlow mounted across the auth boundary so the
  // user's in-flight state (entered name, uploaded photo) survives the
  // transition from logged-out to logged-in. The post-login useEffect
  // bumps us to /app only after profile.onboarding_complete becomes true.
  if (path === '/login') {
    if (!user) return <div className="nlc-frame"><OnboardingFlow startStep="welcome" onExplore={(p)=>navigateTo(p)} /></div>
    if (needsOnboarding) return <div className="nlc-frame"><OnboardingFlow startStep="church" onExplore={(p)=>navigateTo(p)} /></div>
    return null  // fully onboarded — useEffect will navigate to /app
  }

  // ── /app (and any unknown path) ──────────────────────────────────
  // Not logged in → /login (handled by the redirect effect above). Show a
  // loader for the in-between frame rather than a blank screen.
  if (!user) return (
    <div style={{minHeight:'100vh',background:'var(--cream)',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'var(--stone)',fontSize:14,fontFamily:'Plus Jakarta Sans,sans-serif'}}>Loading…</div>
    </div>
  )

  // Signed in but onboarding not finished yet — drop them into the
  // name/photo/church steps. A nullish onboarding_complete is treated as
  // "completed" so the column not existing (or pre-migration users
  // backfilled to true) doesn't trap anyone in the flow.
  // Invitees are the exception: they arrive with full_name + church_id
  // already populated from the invitation and set their photo on the invite
  // screen, so they skip onboarding even before the invite screen finishes
  // persisting onboarding_complete (avoids a flash of the name step).
  if (needsOnboarding) {
    return <div className="nlc-frame"><OnboardingFlow startStep="church" onExplore={(p)=>navigateTo(p)} /></div>
  }

  // Note: onboarding is a soft guide, not a hard gate. The "Find your church"
  // step lets users follow any church (or request one) and skip straight into
  // the Bible reader — no home church is required to use the app.

  // Logged in - show the app
  return <BibleApp />
}

// Top-level crash guard. A render error thrown during a route transition
// (e.g. while the Android hardware back button unwinds the history stack) would
// otherwise blank the WebView and read as an app crash. Catch it, log it, and
// offer a one-tap recovery back into the app instead of a dead screen.
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error) {
    return { error }
  }
  componentDidCatch(error, info) {
    try { console.error('App crashed:', error, info?.componentStack || info) } catch (_e) { /* ignore */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{minHeight:'100vh',background:'var(--cream)',color:'var(--ink)',display:'flex',flexDirection:'column',
          alignItems:'center',justifyContent:'center',gap:18,fontFamily:"'Plus Jakarta Sans',sans-serif",padding:24,textAlign:'center'}}>
          <div style={{fontSize:20,fontWeight:800}}>Something went wrong</div>
          <div style={{fontSize:14,color:'var(--stone)',maxWidth:300,lineHeight:1.5}}>
            The app hit an unexpected error. Tap below to get back to it.
          </div>
          <button type="button"
            onClick={() => { this.setState({ error: null }); navigateTo('/app', { replace: true }) }}
            style={{background:'var(--gold)',color:'var(--ink)',padding:'14px 28px',borderRadius:12,
              fontSize:15,fontWeight:800,border:'none',cursor:'pointer'}}>
            Back to the app
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </AppErrorBoundary>
  )
}
